import React, { type MouseEvent } from 'react'
import Card from '@mui/material/Card'
import { formatDateByUserTimeZone, toUtcDate } from '../../../../helpers/formatDateByUserTimeZone'
import { ReleaseMode } from '../../../../helpers/parseReleaseTimestamp'
import CardContent from '@mui/material/CardContent'
import Typography from '@mui/material/Typography'
import CircularProgress from '@mui/material/CircularProgress'
import Box from '@mui/material/Box'
import { CenteredCardContent, StyledLinearProgressBar } from './styles'
import { useSnackbar } from 'notistack'
import { ADDED_QUESTION_TYPES } from '../../../../consts/addedQuestionTypes'
import ROUTES from '../../../../consts/paths'
import { getPercent } from '../../../../helpers'
import GenericButton from '../../../../components/GenericButton'
import { ButtonSize, ButtonType } from '../../../../components/GenericButton/styles'
import { green, grey, red, shade, yellow } from '../../../../colors'
import { Chip, Divider, useMediaQuery } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { useNavigateToUrl } from '../../../../hooks/useNavigateToUrl'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import RemoveRedEyeOutlinedIcon from '@mui/icons-material/RemoveRedEyeOutlined'
import QrCode2OutlinedIcon from '@mui/icons-material/QrCode2Outlined'
import { SlotStatus } from '../../../../models/DistributionPlan'
import { type InstructorDistribution } from '../../../../models/Distribution'
import { resolveInstructorStudentSurveyShareUrl } from '../../../../helpers/buildPublicSurveyShareUrlFromApi'
import CustomTooltip from '../../../../components/CustomTooltip'
import SandboxBadge from '../../../../components/SandboxBadge'
import GenericModal from '../../../../components/GenericModal'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { AutoImportManager } from '../../../../APIManager'
import { notification } from '../../../../components/Notifications'
import { useUserRole } from '../../../../hooks/useUserRole'
import { ROLES } from '../../../../consts/roles'
import ChangeSurveyDatesModal from '../ChangeSurveyDatesModal'
import {
  INSTRUCTOR_GRADE_POSTING_RELEASE_MESSAGE,
  isInstructorGradePostingReleaseMessage,
  isInstructorReleaseOnGradePostingEnabled
} from '../../../../consts/messages'
export interface InstructorSurveyListCardProps {
  distributionId: string
  distributionSeriesId: string
  surveyNumber: number
  totalSurveys: number
  courseName: string
  sectionAbbr: string
  /** Sandbox mode on the owning Survey Project (CR-341). */
  isSandbox?: boolean
  endDate: string
  startDate: string
  releaseDate: string
  surveyURL?: string | null
  reportURL: string
  studentCount: number
  completionCount: number
  slotNodeStatus: string
  sectionId: string
  slotId: string
  slotTitle: string
  onClick?: () => Promise<void>
  distribution: InstructorDistribution
  isSlotAnonymous?: boolean
  isInstructorAddedQuestionsAllowed?: boolean
  optOutDeadlineTimestamp: string | null
  isInstructorReleaseOnGradePostingEnabled?: boolean
}

enum PrimaryActionButtonText {
  CopyStudentLink = 'Copy Student Link',
  ViewResults = 'View Results'
}

/**
 * @note The Opt-out Deadline is currently hardcoded for demonstration purposes.
 * Backend will provide the actual opt-out deadline value,
 * at which point this hardcoded date should be replaced with dynamic data from the API.
 *
 * When BE integration is available, update this section to:
 * 1. Fetch the opt-out deadline from the backend (API).
 * 2. Use the deadline to determine if the opt-out button should be disabled.
 *
 * 3. Confirm with BE the correct API endpoint and payload for opt-in/opt-out actions.
 *    - See the mutation: `AutoImportManager.updateOptOutOrOptIn`
 *    - Example payload:
 *      {
 *        params: { slotId, objectId: sectionId },
 *        body: { isOptedOut: !isOptedOut }
 *      }
 * 4. Remove or update any hardcoded dates in the UI.
 *
 * Example for disabling the button:
 *   const isOptOutDeadlinePassed = new Date() > new Date(optOutDeadline);
 *   <GenericButton
 *     disabled={isOptOutDeadlinePassed}
 *     ...
 *   />
 */

/**
 * The card's dates, read the way the rest of this feature reads them.
 *
 * Payload timestamps are UTC-naive — `SlotNode.start_timestamp` is `sa.DateTime` with no
 * `timezone=True`, so Marshmallow dumps `2030-12-10T23:59:00` with no offset — and `formatDate`
 * parses that natively, which treats it as the viewer's own clock. The card therefore printed
 * UTC's digits as if they were local, while the Change Survey Dates modal it opens reads the same
 * two props as UTC: the same section, on the same screen, five hours apart in New York. These are
 * instants, shown in the viewer's zone (approved 2026-09-18); the format is unchanged, only the
 * moment it names.
 */
const CARD_DATE_PATTERN = "EEEE, MMM d 'at' h:mm a"

const formatCardDate = (value: string): string => formatDateByUserTimeZone(value, CARD_DATE_PATTERN)

const InstructorSurveyListCard: React.FC<InstructorSurveyListCardProps> = ({
  distributionId,
  distributionSeriesId,
  surveyNumber,
  totalSurveys,
  courseName,
  sectionAbbr,
  isSandbox,
  endDate,
  startDate,
  releaseDate,
  completionCount,
  studentCount,
  surveyURL,
  slotId,
  reportURL,
  sectionId,
  slotNodeStatus,
  slotTitle,
  isSlotAnonymous,
  isInstructorAddedQuestionsAllowed,
  optOutDeadlineTimestamp,
  isInstructorReleaseOnGradePostingEnabled: isGradePostingFlagProp,
  onClick = () => { },
  distribution
}): JSX.Element => {
  // `isOptedOut` is the whole-section aggregate; on a team-taught section it can be false while
  // this instructor has individually opted out. The card's own status/button/toggle must reflect
  // this instructor's state, so read `isOptedOutForCurrentInstructor` for that local `isOptedOut`.
  const { isOptedOutForCurrentInstructor: isOptedOut, isOptedOutByParent, isOptOutable, isOptOutSettingEnabled } = distribution
  const [showOptOutConfirmModal, setShowOptOutConfirmModal] = React.useState<boolean>(false)
  const [showChangeSurveyDatesModal, setShowChangeSurveyDatesModal] = React.useState<boolean>(false)
  const [showReopenWarningModal, setShowReopenWarningModal] = React.useState<boolean>(false)
  // One backend-computed boolean (CR-337). The card does not re-derive the series setting, the
  // permission, the teaching assignment or the status window — two copies of that rule can
  // disagree. Absent → no button, so a backend that does not send it yet surfaces no control.
  const canChangeSurveyDates = Boolean(distribution?.isSurveyDateEditable)
  const isSurveyClosed = slotNodeStatus === SlotStatus.Closed
  // A closed section has its own switch, and its own backend boolean, because every save against
  // one is a reopen rather than a date change. Absent → no button, same as its sibling.
  const canReopenSurvey = Boolean(distribution?.isSurveyReopenAllowed)
  const { enqueueSnackbar } = useSnackbar()
  const navigateToURL = useNavigateToUrl()
  const currDateObj = new Date()
  // Parsed the way the card prints them. A native parse here would read the same no-offset string
  // as the viewer's clock while the text beside it reads UTC, so the button and the time it sits
  // under would disagree by the browser's offset — results looking released hours before the
  // control allows it. Found by review on #1936.
  const releaseDateObj = toUtcDate(releaseDate)
  const endDateStr = formatCardDate(endDate)
  const startDateStr = formatCardDate(startDate)
  const percentValue = isSlotAnonymous ? 0 : getPercent(completionCount, studentCount)
  const progressBarValue = isSlotAnonymous ? 100 : percentValue
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { search } = useLocation()
  const urlParams = new URLSearchParams(search)
  const termId = urlParams.get('termId') ?? ''
  const { hasRole } = useUserRole()

  const isGradePostingMode = isInstructorReleaseOnGradePostingEnabled(isGradePostingFlagProp) ||
    isInstructorReleaseOnGradePostingEnabled(distribution?.isInstructorReleaseOnGradePostingEnabled) ||
    // Rollout fallback only: remove this string-match arm once the BE always supplies the
    // boolean flag on the dashboard payload (avoids a release-date string coincidentally
    // matching the static message and forcing grade-posting mode on legacy data).
    isInstructorGradePostingReleaseMessage(releaseDate)

  // Grade-posting results unlock only when the BE has set a section instructor release date in
  // the past; the aggregate release date does not grant instructor access in this mode.
  const isSectionInstructorReleaseAccessible = (): boolean => {
    const instructorRelease = distribution?.earlyAccessReleaseTimestamp_2
    if (!instructorRelease) return false
    const instructorReleaseDate = toUtcDate(instructorRelease)
    return instructorReleaseDate != null && currDateObj >= instructorReleaseDate
  }

  // Rolling results are not a release instant. The dashboard either flags the project
  // or sends the sentinel release value, and a timestamp on the row (often the section
  // end, still in the future) must not keep View Results disabled after the survey closes.
  const isRollingResults =
    distribution?.isRollingResultsEnabled === true ||
    releaseDate === ReleaseMode.Rolling

  const isInstructorResultsAccessible = isRollingResults || (
    isGradePostingMode
      ? isSectionInstructorReleaseAccessible()
      : releaseDateObj != null && currDateObj >= releaseDateObj
  )

  // The survey for the course section is considered "Released" when its SlotNode status is "Closed"
  // and the current date is on or after the release date (or section instructor release when grade-posting).
  const isSlotNodeReleased = slotNodeStatus === SlotStatus.Closed && isInstructorResultsAccessible
  const queryClient = useQueryClient()

  // Display 'Copy Student Link' when the Survey is in Draft (disabled), Published (enabled), or Live (enabled) states.
  // Display 'View Results' when the Survey is in Closed (disabled) or Released (enabled) states.
  // A Live rolling-results card also shows a separate 'View Results' beside 'Copy Student Link'.

  let releaseDateStr = '-'
  if (releaseDate) {
    if (typeof releaseDate === 'string' && isNaN(Date.parse(releaseDate))) {
      releaseDateStr = releaseDate
    } else {
      releaseDateStr = formatCardDate(releaseDate)
    }
  }

  const primaryActionButtonText = (slotNodeStatus !== SlotStatus.Closed)
    ? PrimaryActionButtonText.CopyStudentLink
    : PrimaryActionButtonText.ViewResults
  const primaryActionButtonType = (slotNodeStatus !== SlotStatus.Closed) ? ButtonType.Secondary : ButtonType.Primary

  const isCopyLink = primaryActionButtonText === PrimaryActionButtonText.CopyStudentLink

  // CR-347: null when the Backend sent no usable link. Copy and QR both need one.
  const surveyShareUrl = resolveInstructorStudentSurveyShareUrl(
    distribution,
    distributionId,
    isSlotAnonymous ?? false
  )
  const hasSurveyLink = Boolean(distributionId) && surveyShareUrl !== null
  const surveyLinkTooltip = slotNodeStatus !== SlotStatus.Live
    ? 'This button will be enabled when the survey is in the Live state.'
    : 'Survey link not available.'

  const isPrimaryActionButtonDisabled =
    (slotNodeStatus === SlotStatus.Draft) ||
    (slotNodeStatus === SlotStatus.Closed && !isInstructorResultsAccessible) ||
    // NEW rules that apply only when the button *is* "Copy Student Link"
    (isCopyLink && (slotNodeStatus !== SlotStatus.Live || !hasSurveyLink))

  // Rolling results are served while the section is still collecting responses, so a Live
  // rolling card offers View Results beside Copy Student Link and Display QR Code rather than
  // instead of them.
  const showsLiveRollingViewResults = isRollingResults && slotNodeStatus === SlotStatus.Live
  const hasReportURL = reportURL.trim() !== ''

  const isPreviewSurveyButtonEnabled = slotNodeStatus !== SlotStatus.Closed
  const isQRCodeButtonEnabled = slotNodeStatus === SlotStatus.Live && hasSurveyLink

  const navigateToReport = (event: MouseEvent<HTMLButtonElement>): void => {
    navigateToURL(event, `${ROUTES.REPORTS}/${reportURL}?termId=${searchParams.get('termId')}&projectId=${distributionSeriesId}`, true)
  }

  const handlePrimaryActionButtonOnClick = (event: MouseEvent<HTMLButtonElement>): void => {
    // Rolling access is already included in isInstructorResultsAccessible, so a closed
    // rolling section is clickable as soon as the button is enabled.
    if (
      slotNodeStatus === SlotStatus.Closed &&
      isInstructorResultsAccessible &&
      hasReportURL
    ) {
      navigateToReport(event)
    } else if ((slotNodeStatus === SlotStatus.Published || slotNodeStatus === SlotStatus.Live) && surveyShareUrl !== null) {
      void navigator.clipboard.writeText(surveyShareUrl).then(() => {
        onClick()
        enqueueSnackbar('Survey link successfully copied!', { variant: 'success' })
      }).catch(error => {
        enqueueSnackbar('Failed to copy to clipboard:', error)
      })
    }
  }
  const handleQRCodeButtonOnClick = (event: MouseEvent<HTMLButtonElement>): void => {
    if (surveyShareUrl === null) return
    const baseURL = window.location.origin
    const qrCodeURL = `${baseURL}${ROUTES.QR_CODE}/?sectionId=${encodeURIComponent(sectionAbbr)}&sectionTitle=${encodeURIComponent(courseName)}&slotNodeStatus=${encodeURIComponent(slotNodeStatus)}&distributionId=${encodeURIComponent(distributionId)}&isSlotAnonymous=${isSlotAnonymous ? 'true' : 'false'}&publicSurveyShareUrl=${encodeURIComponent(surveyShareUrl)}`
    navigateToURL(event, qrCodeURL, true)
  }

  const handlePreviewSurveyOnClick = (event: MouseEvent<HTMLButtonElement>): void => {
    const baseURL = window.location.origin
    const surveyPreviewURL = `${baseURL}${ROUTES.SURVEY_PREVIEW}/${slotId}/${sectionId}`
    navigateToURL(event, surveyPreviewURL, true)
  }

  // CR-295: whether the Course Section question type is on for this project. Read straight from the
  // flag on the payload — it stands for itself, unlike the shared-set pointer this replaced, which
  // only ever meant "some section has questions" and stopped implying enablement when a default
  // template became optional.
  const isSectionTypeEnabled = distribution?.isSectionAddedQuestionsAllowed === true

  // Whether the screen this button opens will actually hold two sets — the same pair of flags the
  // Shared | Mine switcher is gated on. Only then is a single count ambiguous.
  const showsBothQuestionSets = isSectionTypeEnabled && Boolean(isInstructorAddedQuestionsAllowed)
  const ownQuestionCount = typeof distribution?.questionCount === 'number' ? distribution.questionCount : 0

  const handleAddQuestion = (event: MouseEvent<HTMLButtonElement>): void => {
    const questionCount = distribution?.instructorAddedQuestions?.questionCount ?? 0
    const params = new URLSearchParams({
      slotId: distribution?.slotId ?? '',
      divisionId: distribution?.sectionId ?? '',
      questionCount: questionCount.toString(),
      categoryName: distribution?.sectionDetails?.courseName ?? '',
      // Land on whichever set the instructor can actually act on, and default to their own. With
      // both types on, Mine is the safer arrival: an edit there reaches nobody else, and the
      // switcher is how they opt into shared editing deliberately rather than landing in it. The
      // shared set is the landing only where per-instructor questions are off, which makes it the
      // sole set they can edit.
      type: isSectionTypeEnabled && !isInstructorAddedQuestionsAllowed
        ? ADDED_QUESTION_TYPES.SECTION
        : ADDED_QUESTION_TYPES.INSTRUCTOR,
      isInstructorAddedQuestionsAllowed: distribution?.instructorAddedQuestions?.isCustomQuestionAllowed ? 'true' : 'false',
      addedQuestionCount: distribution?.questionCount?.toString() ?? '',
      // The two flags the Shared | Mine switcher is gated on. Both have to be true for the
      // switcher to make sense: offering "Mine" on a project where per-instructor questions are
      // off would invite an instructor to write questions the backend then refuses to store.
      //
      // Note `isInstructorAddedQuestionsAllowed` above is *not* this flag despite the name — it
      // carries `instructorAddedQuestions.isCustomQuestionAllowed`, which is whether new custom
      // questions may be authored. Renaming that param is a separate change; these two are explicit
      // about what they mean instead of overloading it.
      sectionTypeEnabled: isSectionTypeEnabled ? 'true' : 'false',
      instructorSetAvailable: isInstructorAddedQuestionsAllowed ? 'true' : 'false',
      termId: termId ?? ''
    })
    navigate(`${ROUTES.ACADEMIC_UNIT_ADDED_QUESTIONS}?${params.toString()}`)
  }

  const isTAOrStaff = hasRole(ROLES.TEACHING_ASSISTANT) || hasRole(ROLES.SUPPORT_STAFF)
  // The Course Section type being on is reason enough to open the screen even when per-instructor
  // questions are off: otherwise a project using only the section-owned type has questions its
  // instructors can never author or see. Status and role gating are unchanged.
  const isDisabledAddQuestionButton = (!isInstructorAddedQuestionsAllowed && !isSectionTypeEnabled) ||
    (slotNodeStatus !== SlotStatus.Published) || isTAOrStaff

  const addQuestionButtonTooltipTitle = (): string => {
    if (isDisabledAddQuestionButton) {
      return 'The ability to add additional questions is disabled for this Survey'
    }

    return ''
  }

  const renderSurveyDateDetails = (
    startDateStr: string,
    endDateStr: string
  ): JSX.Element => {
    try {
      switch (slotNodeStatus) {
        case SlotStatus.Draft:
          return (
            <Typography fontWeight='500' fontSize='14px' color={grey[900]} variant="caption">
              Survey Starts: <b>{startDateStr}</b>
            </Typography>
          )
        case SlotStatus.Published:
          return (
            <Typography fontWeight='500' fontSize='14px' color={grey[900]} variant="caption">
              Survey Starts: <b>{startDateStr}</b>
            </Typography>
          )
        case SlotStatus.Live:
          return (
            <Typography fontWeight='500' fontSize='14px' color={grey[900]} variant="caption">
              Survey Ends: <b>{endDateStr}</b>
            </Typography>
          )
        case SlotStatus.Closed:
          if (isGradePostingMode) {
            return (
              <Typography fontWeight='500' fontSize='14px' color={grey[900]} variant="caption">
                Results Released: <b><em>{INSTRUCTOR_GRADE_POSTING_RELEASE_MESSAGE}</em></b>
              </Typography>
            )
          }
          return (
            <Typography fontWeight='500' fontSize='14px' color={grey[900]} variant="caption">
              Results Released: <b>{releaseDateStr}</b>
            </Typography>
          )
        default:
          return <></>
      }
    } catch (error) {
      return <></>
    }
  }

  const { mutate: handleOptoutOrOptIn, isPending: isOptoutOrOptInPinding } = useMutation({
    mutationFn: AutoImportManager.updateOptOutOrOptIn,
    onError: (error: any) => {
      notification(error?.message ?? 'Optout or optIn error', { variant: 'error' })
    },
    meta: { silent: true },

    onSuccess: () => {
      notification(`Successfully ${isOptedOut ? 'opted in' : 'opted out'}`, { variant: 'success' })
      setShowOptOutConfirmModal(false)
      void queryClient.invalidateQueries({ queryKey: ['instructorDistributions'] })
    }
  })
  const handleOptOut = (): void => {
    const payload = {
      params: {
        slotId,
        objectId: sectionId
      },
      body: {
        isOptedOut: !isOptedOut
      }
    }
    handleOptoutOrOptIn(payload)
  }

  const optOutDeadlineDate = toUtcDate(optOutDeadlineTimestamp)
  const isOptOutDeadlinePassed = optOutDeadlineDate != null && new Date() > optOutDeadlineDate

  const optOutButtonTooltipTitle = (): string => {
    if (isOptOutDeadlinePassed) {
      return 'The opt out deadline has passed.'
    }

    return ''
  }

  return (
    <Card tabIndex={0} aria-label={`survey ${surveyNumber} of ${totalSurveys} ${courseName}`} style={{ marginBottom: '2rem' }}>
      {
        slotNodeStatus === null
          ? (
            // Render a circular loader when slotNodeStatus is null
            <CenteredCardContent>
              <CircularProgress />
            </CenteredCardContent>
            )
          : (
            // Render the card content when surveyState is not null
            <>
              <CardContent >
                <Box display="flex" justifyContent="space-between" >
                  <Box>
                    <Box display={'flex'} gap={3} alignItems={'center'}>
                      <Typography variant="p4">{slotTitle}</Typography>
                      {isOptOutSettingEnabled && <Typography fontSize={12} fontWeight={600} color={isOptedOut ? red[950] : green[800]}>{isOptedOut ? '• Opted-out' : '• Opted-in'}</Typography>}
                    </Box>
                    <Box display="flex" alignItems="center" gap={4}>
                      <Typography mt={2} variant="h5" gutterBottom>
                        {sectionAbbr}
                      </Typography>
                      {/* Beside the section, not the Survey Project title above it: sandbox is a
                          property of the term this section is running in, and the row the reader
                          acts on is the section. */}
                      <SandboxBadge isSandbox={isSandbox} />
                      {isSlotAnonymous && (
                        <Chip
                          label="Unverified Submissions"
                          variant="filled"
                          size="small"
                          sx={{
                            borderRadius: '6px',
                            border: `1px solid ${yellow[100]}`,
                            fontWeight: 500,
                            fontSize: '12px',
                            padding: '0px 8px',
                            color: red[800],
                            backgroundColor: shade[1000]
                          }}
                        />
                      )}
                    </Box>
                    <Typography variant="p4">
                      {courseName}
                    </Typography>
                  </Box>
                  <Divider />
                  {
                    !isMobile &&
                    <Box display="flex" gap={2} alignItems="flex-start">
                      <CustomTooltip
                        title={
                          isPrimaryActionButtonDisabled && isCopyLink
                            ? surveyLinkTooltip
                            : ''
                        }
                      >

                        <GenericButton
                          buttonType={primaryActionButtonType}
                          label={primaryActionButtonText}
                          fontWeight='600'
                          buttonSize={ButtonSize.Medium}
                          onClick={handlePrimaryActionButtonOnClick}
                          disabled={isPrimaryActionButtonDisabled}
                        />
                      </CustomTooltip>
                      {showsLiveRollingViewResults &&
                        <GenericButton
                          buttonType={ButtonType.Primary}
                          label={PrimaryActionButtonText.ViewResults}
                          fontWeight='600'
                          buttonSize={ButtonSize.Medium}
                          onClick={navigateToReport}
                          disabled={!hasReportURL}
                        />
                      }
                    </Box>
                  }

                </Box>
              </CardContent>
              <CardContent aria-label={`Survey closed${percentValue}% completed ${completionCount}/${studentCount} Responses`} tabIndex={0}>
                <Box display='flex' justifyContent='space-between'>
                  {renderSurveyDateDetails(startDateStr, endDateStr)}
                </Box>

                {isOptOutSettingEnabled && optOutDeadlineTimestamp &&
                  <Typography fontWeight='500' fontSize='14px' color={grey[900]} variant="caption">Opt-out Deadline: <b>{optOutDeadlineTimestamp && formatCardDate(optOutDeadlineTimestamp)}</b></Typography>
                }
                <Box padding={theme.spacing(2)}>
                  <StyledLinearProgressBar value={progressBarValue} isNotClosed={slotNodeStatus !== SlotStatus.Closed} isAnonymous={isSlotAnonymous} />
                </Box>
                <Box display='flex' justifyContent='space-between'>
                  <Typography color={grey[950]} fontWeight='500' component="h4" variant="caption">
                    {percentValue}% Complete
                  </Typography>
                  <Typography color={grey[950]} fontWeight='500' component="h4" variant="caption">
                    {isSlotAnonymous ? `${completionCount} Responses` : `${completionCount}/${studentCount} Responses`}
                  </Typography>
                </Box>
                <Divider sx={{ padding: 2 }} />
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: isMobile ? 1 : 2,
                    mt: 6,
                    flexDirection: isMobile ? 'column' : 'row',
                    width: isMobile ? '100%' : 'auto'
                  }}
                >
                  <GenericButton
                    buttonType={ButtonType.Secondary}
                    label="Preview Survey"
                    fontWeight='600'
                    startIcon={<RemoveRedEyeOutlinedIcon />}
                    buttonSize={ButtonSize.Medium}
                    onClick={handlePreviewSurveyOnClick}
                    disabled={!isPreviewSurveyButtonEnabled}
                    fullWidth={isMobile}
                  />
                  <CustomTooltip
                    title={slotNodeStatus === SlotStatus.Live && !hasSurveyLink ? surveyLinkTooltip : ''}
                  >
                    <GenericButton
                      buttonType={ButtonType.Secondary}
                      label="Display QR Code"
                      fontWeight='600'
                      startIcon={<QrCode2OutlinedIcon />}
                      buttonSize={ButtonSize.Medium}
                      onClick={handleQRCodeButtonOnClick}
                      disabled={!isQRCodeButtonEnabled}
                      fullWidth={isMobile}
                    />
                  </CustomTooltip>
                  <CustomTooltip title={addQuestionButtonTooltipTitle()}>
                    <GenericButton
                      buttonType={ButtonType.Secondary}
                      // The count only ever reported *this instructor's own* added questions, so it
                      // drops off once the screen holds two sets: a single number beside "Manage"
                      // would describe one of them without saying which, and the switcher labels
                      // both. Where only one set is reachable there is nothing to disambiguate, and
                      // dropping it would just take information off the dashboard — including on
                      // every project until the backend serializes
                      // `isSectionAddedQuestionsAllowed`, which is what makes the switcher appear
                      // at all.
                      label={!showsBothQuestionSets && ownQuestionCount > 0
                        ? `Manage Questions (${ownQuestionCount})`
                        : 'Manage Questions'}
                      fontWeight='600'
                      buttonSize={ButtonSize.Medium}
                      onClick={handleAddQuestion}
                      disabled={isDisabledAddQuestionButton}
                      fullWidth={isMobile}
                    />
                  </CustomTooltip>
                  {(isSurveyClosed ? canReopenSurvey : canChangeSurveyDates) &&
                    <GenericButton
                      buttonType={ButtonType.Secondary}
                      // Named for what the click does. On a closed section the only save available
                      // is a reopen, so calling it a date change would undersell it; everywhere
                      // else a reopen is not on the table at all.
                      label={isSurveyClosed ? 'Reopen Survey' : 'Change Survey Dates'}
                      fontWeight='600'
                      buttonSize={ButtonSize.Medium}
                      onClick={() => {
                        // On a Closed section every save is a reopen, and a reopen costs more than
                        // a date: it pushes the results release date out, hides the reports that
                        // exist for the section, and regenerates them only after the new end date.
                        // That is told first, before any date is picked — the modal behind this
                        // one repeats it beside the date the instructor chooses.
                        if (slotNodeStatus === SlotStatus.Closed) {
                          setShowReopenWarningModal(true)
                          return
                        }
                        setShowChangeSurveyDatesModal(true)
                      }}
                      fullWidth={isMobile}
                    />
                  }
                  {isOptOutSettingEnabled &&
                    <CustomTooltip title={optOutButtonTooltipTitle()}>
                      <GenericButton
                        buttonType={isOptedOut ? ButtonType.Primary : ButtonType.Destructive}
                        label={isOptedOut ? 'Opt In' : 'Opt Out'}
                        fontWeight='600'
                        buttonSize={ButtonSize.Medium}
                        fullWidth={isMobile}
                        disabled={isOptOutDeadlinePassed || !isOptOutable}
                        onClick={() => { setShowOptOutConfirmModal(true) }}
                      />
                    </CustomTooltip>
                  }

                </Box>
                <GenericModal
                  isOpen={showReopenWarningModal}
                  onClose={() => { setShowReopenWarningModal(false) }}
                  title={`Reopen ${sectionAbbr}?`}
                  confirmActionText="Continue"
                  maxWidth='sm'
                  showCloseIcon={false}
                  onConfirm={async () => {
                    setShowReopenWarningModal(false)
                    setShowChangeSurveyDatesModal(true)
                  }}>
                  {/* Three series shapes, and the warning has to agree with the modal behind it:
                      grade posting says the administrator decides when results appear, rolling
                      results has no release date at all (they open as soon as the section closes),
                      and only the default derives one from the end date. Asserting the default for
                      all three had instructors acknowledging something the next screen denied.
                      Found by review on #1936. Grade posting is tested first, as it is in the
                      modal, so a series carrying both flags reads the same in both places. */}
                  {isGradePostingMode
                    ? (
                      <>
                        <Typography mb={1}>
                          This survey has closed. Reopening it collects responses again.
                        </Typography>
                        <Typography mb={1}>
                          Your administrator releases results after grades are posted, so reopening
                          does not change when you see them. The reports that already exist for this
                          course section will be hidden until the survey closes again on the new end
                          date.
                        </Typography>
                      </>
                      )
                    : distribution?.isRollingResultsEnabled === true
                      ? (
                        <>
                          <Typography mb={1}>
                            This survey has closed. Reopening it collects responses again.
                          </Typography>
                          <Typography mb={1}>
                            Results for this course section are available once it closes, so
                            reopening hides the reports that exist now. They come back when the
                            survey closes again on the new end date.
                          </Typography>
                        </>
                        )
                      : (
                        <>
                          <Typography mb={1}>
                            This survey has closed. Reopening it collects responses again and moves
                            its results release date out to match the new end date.
                          </Typography>
                          <Typography mb={1}>
                            The reports that already exist for this course section will be hidden. A
                            fresh set is generated once the survey closes again on the new end date,
                            and becomes visible on the new release date.
                          </Typography>
                        </>
                        )}
                  <Typography>
                    You&apos;ll choose the new end date next.
                  </Typography>
                </GenericModal>
                {showChangeSurveyDatesModal && (
                  <ChangeSurveyDatesModal
                    isOpen={showChangeSurveyDatesModal}
                    onClose={() => { setShowChangeSurveyDatesModal(false) }}
                    slotId={slotId}
                    sectionId={sectionId}
                    sectionAbbr={sectionAbbr}
                    slotNodeStatus={slotNodeStatus}
                    startTimestamp={startDate}
                    endTimestamp={endDate}
                    isRollingResultsEnabled={distribution?.isRollingResultsEnabled}
                    isInstructorReleaseOnGradePostingEnabled={isGradePostingMode}
                    canApplyToCrossListed={distribution?.canApplyToCrossListed}
                    projectStartTimestamp={distribution?.slotStartTimestamp}
                    projectEndTimestamp={distribution?.slotEndTimestamp}
                    projectReleaseTimestamp={distribution?.slotReleaseTimestamp}
                  />
                )}
                <GenericModal
                  isOpen={showOptOutConfirmModal}
                  onClose={() => { setShowOptOutConfirmModal(false) }}
                  title={`Confirm ${isOptedOut ? 'Opt-In' : 'Opt-Out'}  for ${sectionAbbr}`}
                  confirmButtonLoading={isOptoutOrOptInPinding}
                  maxWidth='sm'
                  showCloseIcon={false}
                  onConfirm={async () => { handleOptOut() }}>
                  <Typography mb={1}>
                    Are you sure you want to {isOptedOut ? 'opt in to' : 'opt out of'} this course evaluation? By confirming, you acknowledge that you have permission to do so.
                  </Typography>
                </GenericModal>
                {
                  isMobile &&
                  <CustomTooltip
                    title={
                      isPrimaryActionButtonDisabled && isCopyLink
                        ? surveyLinkTooltip
                        : ''
                    }
                  >
                    <span>
                      <GenericButton
                        fullWidth
                        buttonType={primaryActionButtonType}
                        label={primaryActionButtonText}
                        fontWeight='600'
                        buttonSize={ButtonSize.Medium}
                        style={{ marginTop: theme.spacing(5) }}
                        onClick={handlePrimaryActionButtonOnClick}
                        disabled={isPrimaryActionButtonDisabled}
                      />
                    </span>
                  </CustomTooltip>
                }
                {isMobile && showsLiveRollingViewResults &&
                  <GenericButton
                    fullWidth
                    buttonType={ButtonType.Primary}
                    label={PrimaryActionButtonText.ViewResults}
                    fontWeight='600'
                    buttonSize={ButtonSize.Medium}
                    style={{ marginTop: theme.spacing(2) }}
                    onClick={navigateToReport}
                    disabled={!hasReportURL}
                  />
                }
              </CardContent>
            </>
            )}
    </Card>
  )
}
export default InstructorSurveyListCard
